import { Flex, Loader, Paper } from '@mantine/core';
import {
  ChatLoadError,
  ChatLoadMoreTrigger,
  ChatScrollArea,
} from '@packages/chat/ui';
import { useCallback } from 'react';
import { useUserSintiaListenVoiceMessage } from './useUserSintiaListenVoiceMessage';
import type { UserSintiaChat } from './useUserSintiaChat';
import { UserSintiaChatReadonlyMessage } from './user-sintia-chat-readonly-message';
import classes from './user-sintia-chat-readonly-message-list.module.scss';

const RETRY_LABEL = 'Повторить';

type UserSintiaChatReadonlyMessageListProps = {
  chat: Pick<UserSintiaChat, 'items' | 'history' | 'scrollToLatestKey'>;
};

export const UserSintiaChatReadonlyMessageList = ({
  chat: { items, history, scrollToLatestKey },
}: UserSintiaChatReadonlyMessageListProps) => {
  const { openVoiceMessage } = useUserSintiaListenVoiceMessage();
  const { initialLoad, history: olderHistory } = history;

  const handleOpenModal = useCallback(
    (audioUrl: string, fileName: string) => {
      openVoiceMessage({
        type: 'listen',
        audioUrl,
        fileName,
        // The chat owns the audio URLs of its messages.
        onClose: () => undefined,
      });
    },
    [openVoiceMessage]
  );

  if (initialLoad.error) {
    return (
      <Paper bg='primary.0' px={16} py={16} flex='1'>
        <ChatLoadError
          message='Не удалось загрузить переписку'
          retryLabel={RETRY_LABEL}
          isRetrying={initialLoad.isFetching}
          onRetry={() => void initialLoad.retry()}
        />
      </Paper>
    );
  }

  if (initialLoad.isPending && items.length === 0) {
    return (
      <Paper bg='primary.0' px={16} py={16} flex='1'>
        <Flex justify='center' p='xl'>
          <Loader size='sm' />
        </Flex>
      </Paper>
    );
  }

  return (
    <Paper bg='primary.0' px={16} py={16} flex='1' mih={320}>
      <ChatScrollArea
        gap={24}
        h='100%'
        mih={280}
        mah={450}
        className={classes.wrapper}
        loadMore={{
          hasMore: olderHistory.hasMore,
          isLoading: olderHistory.isFetching,
          hasError: Boolean(olderHistory.error),
          onLoadMore: olderHistory.loadMore,
        }}
        scrollToLatestKey={scrollToLatestKey}
      >
        {items.map(({ key, ...item }) => (
          <UserSintiaChatReadonlyMessage
            key={key}
            {...item}
            handleOpenModal={handleOpenModal}
          />
        ))}
        <ChatLoadMoreTrigger />
        {olderHistory.error && (
          <ChatLoadError
            message='Не удалось загрузить предыдущие сообщения'
            retryLabel={RETRY_LABEL}
            isRetrying={olderHistory.isFetching}
            onRetry={() => void olderHistory.loadMore()}
          />
        )}
      </ChatScrollArea>
    </Paper>
  );
};
